// ============================================================
// Reservation effective status
//
// A reservation's status is not simply the `status` column. It is derived from
// how many units have actually been Posted through assembly orders, how many
// are still moving through the pipeline, and only then the stored column.
//
// This lived twice - in ReservationsPage (list) and ReservationDetailPage - and
// the two copies had drifted apart, so the same reservation could read
// "In Progress" in the list and "Partial" on its own page. Extracted here so
// there is one rule, and so it can be tested.
// ============================================================

export type EffectiveStatus =
  | 'Pending'
  | 'In Progress'
  | 'Partial'
  | 'Completed'
  | 'Cancelled';

/**
 * Assembly-order statuses that count as "work is underway".
 *
 * This previously carried 'In Progress' as well, described as a legacy spelling
 * still present in older rows. That was wrong: assembly_orders.status has a
 * validated CHECK constraint that does not permit it, so no row can hold it and
 * the branch never matched. Now deferring to the single list.
 */
import { PIPELINE_STATUSES, isActiveOrderStatus } from '@/lib/assemblyOrderStatus';

export { PIPELINE_STATUSES as ACTIVE_AO_STATUSES, isActiveOrderStatus as isActiveAOStatus };

/**
 * Units delivered against a reservation.
 *
 * Two sources disagree in practice: assembly orders that reached 'Posted', and
 * the denormalised `quantity_fulfilled` column. Take the larger - a unit that
 * either source believes was delivered was delivered, and understating
 * fulfilment is the more harmful error (it re-opens finished work).
 */
export function resolveFulfilledUnits(
  postedUnits: number,
  quantityFulfilled: number | null | undefined
): number {
  return Math.max(postedUnits || 0, quantityFulfilled || 0);
}

/**
 * The single status rule.
 *
 * Order matters, and only for one case: a reservation with some units Posted
 * and others still on the bench satisfies both "Partial" and "In Progress".
 * In Progress wins - what the floor is doing now is the more useful thing to
 * surface, and it is what the list view has always shown.
 */
export function deriveReservationStatus(
  dbStatus: string,
  quantity: number,
  fulfilled: number,
  activeUnits: number
): EffectiveStatus {
  if (dbStatus === 'Cancelled') return 'Cancelled';
  if (fulfilled >= quantity && quantity > 0) return 'Completed';
  if (activeUnits > 0) return 'In Progress';
  if (fulfilled > 0) return 'Partial';
  return 'Pending';
}

/** A reservation in a terminal state cannot be edited or cancelled. */
export function isTerminalStatus(status: EffectiveStatus): boolean {
  return status === 'Completed' || status === 'Cancelled';
}

export interface AssemblyOrderLike {
  status?: string | null;
  quantity?: number | null;
}

export interface ReservationUnitTotals {
  /** Units on assembly orders that reached 'Posted'. */
  postedUnits: number;
  /** Units on assembly orders still moving through the pipeline. */
  activeUnits: number;
}

/**
 * Sum assembly-order quantities into posted and active buckets.
 *
 * A missing quantity counts as one unit - that is how the rest of the app has
 * always treated legacy rows written before the column existed.
 */
export function sumAssemblyOrderUnits(
  orders: readonly AssemblyOrderLike[] | null | undefined
): ReservationUnitTotals {
  let postedUnits = 0;
  let activeUnits = 0;

  for (const order of orders ?? []) {
    const units = order.quantity || 1;
    if (order.status === 'Posted') {
      postedUnits += units;
    } else if (isActiveOrderStatus(order.status)) {
      activeUnits += units;
    }
  }

  return { postedUnits, activeUnits };
}
